import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Play, Pause } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ImageCropUpload } from './ImageCropUpload';
import { logger } from '../utils/logger';
import { BACKEND_URL } from '../utils/api';
import { ANIMAL_ICONS } from '../theme';
import { SovereignsPath } from './try/SovereignsPath';

/* ── PILLAR accent palette — the little "Fashion Path" / "Taste Path" /
   … book button lives next to Add Vibe on every pillar tab.  Colour
   matches PillarMembersModal / PillarMasterCover for visual continuity. */
const PILLAR_ACCENT = {
  fashion: { label: 'Fashion', accent: '#D4AF37', soft: '#F5D97A', ink: '#3E2A08' },
  food:    { label: 'Taste',   accent: '#C8A95E', soft: '#E8CE94', ink: '#3E2E12' },
  explore: { label: 'Explore', accent: '#0F52BA', soft: '#4A85E8', ink: '#DDE9FF' },
  souls:   { label: 'Soul',    accent: '#DC143C', soft: '#FF4A6A', ink: '#FFD8DE' },
  sporty:  { label: 'Sport',   accent: '#10B981', soft: '#4FE8B4', ink: '#D6FFEE' },
  success: { label: 'Success', accent: '#E5C44D', soft: '#F5DE85', ink: '#3E2E08' },
  legacy:  { label: 'Legacy',  accent: '#8B5CF6', soft: '#B08CFF', ink: '#F0E5FF' },
  art:     { label: 'Art',     accent: '#2A9BD8', soft: '#5BC0F0', ink: '#DDF3FF' },
};

const memoryCache = {};

const VideoCard = ({ memory, onLongPress, onCancelPress, index }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = 0.4;
    video.muted = true;
    video.loop = true;
    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
    return () => { video.pause(); };
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.playbackRate = 0.4; video.muted = true; setPlaying(false);
    } else {
      video.playbackRate = 1; video.muted = false; video.currentTime = 0;
      video.play().catch(() => {}); setPlaying(true);
    }
  };

  return (
    <div
      className="memory-card group relative"
      style={{ height: Math.random() * 100 + 300 + 'px' }}
      onTouchStart={() => onLongPress(memory.id)} onTouchEnd={onCancelPress} onTouchMove={onCancelPress}
      onMouseDown={() => onLongPress(memory.id)} onMouseUp={onCancelPress} onMouseLeave={onCancelPress}
      data-testid={`memory-card-${index}`}
    >
      <video ref={videoRef} src={memory.image_url} className={playing ? 'playing' : 'slowmo'} playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <button onClick={togglePlay} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center z-10 hover:bg-black/70 transition-colors" data-testid={`video-play-btn-${index}`}>
        {playing ? <Pause size={18} className="text-white" /> : <Play size={18} className="text-white ml-0.5" />}
      </button>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="font-['Outfit'] text-white text-sm leading-relaxed">{memory.note}</p>
          {memory.vibe_date && <span className="inline-block mt-1.5 text-[10px] text-[#D4AF37]/60 font-['Outfit'] italic">{memory.vibe_date}</span>}
        </div>
      </div>
    </div>
  );
};

export const MemoryGrid = ({ category }) => {
  const [memories, setMemories] = useState(() => memoryCache[category] || []);
  const [loading, setLoading] = useState(!memoryCache[category]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPillarPath, setShowPillarPath] = useState(false);
  const pillar = PILLAR_ACCENT[category] || PILLAR_ACCENT.fashion;
  const { user } = useAuth();
  const mounted = useRef(true);
  const longPressTimer = useRef(null);

  const loadMemories = useCallback(async () => {
    if (!memoryCache[category]) setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/memories?category=${category}`);
      memoryCache[category] = data;
      if (mounted.current) { setMemories(data); setLoading(false); }
    } catch { if (mounted.current) setLoading(false); }
  }, [category]);

  useEffect(() => { mounted.current = true; loadMemories(); return () => { mounted.current = false; }; }, [loadMemories]);

  const handleDelete = async (memoryId) => {
    if (!window.confirm('Are you sure you want to delete this vibe?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/memories/${memoryId}`);
      const updated = memories.filter((m) => m.id !== memoryId);
      setMemories(updated); memoryCache[category] = updated;
    } catch (error) { logger.error('Failed to delete memory:', error); }
  };

  const startLongPress = (memoryId) => { longPressTimer.current = setTimeout(() => handleDelete(memoryId), 600); };
  const cancelLongPress = () => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } };
  const handleUploadSuccess = () => { loadMemories(); };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const animalIcon = ANIMAL_ICONS[category];

  /* ── Per-category accent palette for the Polaroid-style frame.
        Replaces the white photo border with the chapter's royal-leather color. */
  const CARD_THEME = {
    fashion: { frame: 'linear-gradient(180deg,#3a2510 0%,#2a1908 100%)', accent: '#D4AF37' },
    food:    { frame: 'linear-gradient(180deg,#322610 0%,#241a08 100%)', accent: '#E8D88C' },
    explore: { frame: 'linear-gradient(180deg,#101e3c 0%,#0a1428 100%)', accent: '#5B8BD8' },
    souls:   { frame: 'linear-gradient(180deg,#3a0e1a 0%,#2a070e 100%)', accent: '#DC143C' },
    sporty:  { frame: 'linear-gradient(180deg,#0e2a1d 0%,#0a1f15 100%)', accent: '#10B981' },
    success: { frame: 'linear-gradient(180deg,#202024 0%,#16161a 100%)', accent: '#D9D9D9' },
    legacy:  { frame: 'linear-gradient(180deg,#221530 0%,#180f24 100%)', accent: '#8B5CF6' },
    art:     { frame: 'linear-gradient(180deg,#0e1530 0%,#080f24 100%)', accent: '#3D8BFF' },
  };
  const theme = CARD_THEME[category] || CARD_THEME.art;

  return (
    <div className="pb-4" data-testid={`memory-grid-${category}`}>
      {/* 3D Statue Rotation — category icons only */}
      <style>{`
        @keyframes statueRotate {
          from { transform: rotateY(0deg); }
          to   { transform: rotateY(360deg); }
        }
        .category-icon-wrap {
          perspective: 1000px;
        }
        .category-icon {
          transform-style: preserve-3d;
          animation: statueRotate 8s linear infinite;
        }
      `}</style>

      {/* Category Header — sticky.  Icon + title + compact Add Vibe button
          are ALL grouped on the left; Add Vibe now sits directly beside the
          title (per user request Feb 2026 — was previously right-aligned). */}
      <div className="sticky top-0 px-4 py-3 flex items-center gap-2" style={{ background: '#000', zIndex: 19 }}>
        {animalIcon && (
          <div className="category-icon-wrap flex-shrink-0">
            <div className="category-icon w-9 h-9 rounded-full overflow-hidden border border-[#D4AF37]/20"
              style={{
                boxShadow: '0 0 15px rgba(212,175,55,0.15)',
                filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.6))',
              }}>
              <img src={animalIcon.src} alt={category} className="w-full h-full object-cover" style={{ objectPosition: animalIcon.pos }} />
            </div>
          </div>
        )}
        <h2 className="font-['Cormorant_Garamond'] font-light text-white capitalize tracking-wide min-w-0 truncate"
          style={{ fontSize: 'clamp(18px, 5.2vw, 24px)' }}>
          My {category === 'food' ? 'Taste' : category === 'souls' ? 'Soul' : category === 'sporty' ? 'Sport' : category} Vibes
        </h2>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] text-black rounded-full font-['Outfit'] font-medium hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all duration-300 flex-shrink-0"
          style={{ fontSize: 13 }}
          data-testid={`add-memory-button-${category}`}
          aria-label="Add vibe"
        >
          <Plus size={15} />
          <span>Add Vibe</span>
        </button>
        {/* Pillar Path — this pillar's own book, coloured by the pillar accent.
            Opens ONLY this pillar's chronicles as a paginated volume. */}
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => setShowPillarPath(true)}
          data-testid={`pillar-path-button-${category}`}
          aria-label={`Open ${pillar.label} Path`}
          className="flex items-center gap-1 pl-1.5 pr-2.5 py-1.5 rounded-full font-['Cormorant_Garamond'] italic flex-shrink-0"
          style={{
            fontSize: 12,
            background: `linear-gradient(135deg, ${pillar.accent}30, ${pillar.accent}10)`,
            border: `1px solid ${pillar.accent}88`,
            color: pillar.soft,
            boxShadow: `0 0 10px ${pillar.accent}44, inset 0 0 6px ${pillar.accent}22`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id={`pp-book-${category}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor={pillar.soft} />
                <stop offset="55%" stopColor={pillar.accent} />
                <stop offset="100%" stopColor={pillar.accent} stopOpacity="0.55" />
              </linearGradient>
            </defs>
            <path d="M4 4.5 C4 3.7 4.7 3 5.5 3 H18.5 C19.3 3 20 3.7 20 4.5 V19.5 C20 20.3 19.3 21 18.5 21 H5.5 C4.7 21 4 20.3 4 19.5 Z"
                  stroke={`url(#pp-book-${category})`} strokeWidth="1.4" fill="rgba(10,10,16,0.55)"/>
            <path d="M8 6 H16 M8 9 H16 M8 12 H16" stroke={`url(#pp-book-${category})`} strokeWidth="0.9" strokeLinecap="round" opacity="0.7"/>
            {/* Spine ribbon coloured with pillar accent */}
            <path d="M11.5 3 V15 L12.5 13.5 L13.5 15 V3" fill={pillar.accent} stroke={pillar.soft} strokeWidth="0.4"/>
            <circle cx="12" cy="18" r="1.15" fill={`url(#pp-book-${category})`}/>
          </svg>
          <span>{pillar.label} Path</span>
        </motion.button>
      </div>

      {memories.length === 0 ? (
        <div className="text-center py-20">
          {animalIcon && (
            <div className="category-icon-wrap">
              <div className="category-icon w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border border-[#D4AF37]/20 opacity-40"
                style={{
                  boxShadow: '0 0 20px rgba(212,175,55,0.1)',
                  filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.6))',
                }}>
                <img src={animalIcon.src} alt={category} className="w-full h-full object-cover" style={{ objectPosition: animalIcon.pos }} />
              </div>
            </div>
          )}
          <p className="font-['Outfit'] text-white/30 mb-4">No vibes yet</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] rounded-full font-['Outfit'] font-medium hover:bg-[#D4AF37]/10 hover:shadow-[0_0_15px_rgba(212,175,55,0.15)] transition-all duration-300"
            data-testid={`first-memory-button-${category}`}
          >
            Create Your First Vibe
          </button>
        </div>
      ) : (
        <div className="masonry-grid">
          {memories.map((memory, index) =>
            memory.media_type === 'video' ? (
              <VideoCard key={memory.id || index} memory={memory} onLongPress={startLongPress} onCancelPress={cancelLongPress} index={index} />
            ) : memory.media_type === 'audio' ? (
              <div key={memory.id || index} className="memory-card group relative" style={{ height: Math.random() * 100 + 300 + 'px' }}
                onTouchStart={() => startLongPress(memory.id)} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress}
                onMouseDown={() => startLongPress(memory.id)} onMouseUp={cancelLongPress} onMouseLeave={cancelLongPress}
                data-testid={`memory-card-${index}`}
              >
                <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/5 to-[#05050A] flex flex-col items-center justify-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                  </div>
                  <audio src={memory.image_url} controls className="w-4/5" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="font-['Outfit'] text-white text-sm leading-relaxed">{memory.note}</p>
                    {memory.vibe_date && <span className="inline-block mt-1.5 text-[10px] text-[#D4AF37]/60 font-['Outfit'] italic">{memory.vibe_date}</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={memory.id || index}
                className="group relative active:scale-[0.98] transition-transform"
                onTouchStart={() => startLongPress(memory.id)} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress}
                onMouseDown={() => startLongPress(memory.id)} onMouseUp={cancelLongPress} onMouseLeave={cancelLongPress}
                data-testid={`memory-card-${index}`}
                style={{
                  /* Polaroid frame — replaces the white border with the
                     chapter's royal-leather color. */
                  background: theme.frame,
                  border: `1.5px solid ${theme.accent}`,
                  borderRadius: 8,
                  padding: '12px 12px 14px',
                  boxShadow:
                    `inset 0 0 0 1px ${theme.accent}55,` +
                    `0 0 14px ${theme.accent}44,` +
                    `0 10px 22px rgba(0,0,0,0.6)`,
                }}
              >
                {/* Image well — recessed inside the gold-trimmed mat */}
                <div className="relative overflow-hidden" style={{
                  borderRadius: 4,
                  aspectRatio: '4 / 5',
                  background: '#05050A',
                  boxShadow: `inset 0 0 0 1px ${theme.accent}aa, inset 0 0 12px rgba(0,0,0,0.6)`,
                }}>
                  <img src={memory.image_url} alt={memory.note || 'vibe'} loading="lazy"
                    className="w-full h-full object-cover"/>
                </div>

                {/* Caption band — horizontal: 90% vibes note · 10% date.
                    The date is the memory's automatic upload timestamp
                    (server-set `created_at`), never user-entered. */}
                <div className="mt-2.5 flex items-stretch gap-2"
                  style={{
                    minHeight: 56,
                    borderTop: `1px solid ${theme.accent}55`,
                    paddingTop: 8,
                  }}>
                  <div className="flex-1 min-w-0 flex items-center" style={{ flexBasis: '90%' }}>
                    {memory.note ? (
                      <p className="font-['Cormorant_Garamond'] italic leading-snug line-clamp-3 w-full"
                        style={{ color: '#F5E6A0', fontSize: 14 }}>
                        {memory.note}
                      </p>
                    ) : (
                      <p className="font-['Cormorant_Garamond'] italic leading-snug w-full"
                        style={{ color: 'rgba(245,217,122,0.35)', fontSize: 14 }}>
                        — no note —
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-center justify-center px-1.5 text-center"
                    style={{
                      flexBasis: '10%',
                      minWidth: 44,
                      borderLeft: `1px solid ${theme.accent}55`,
                    }}>
                    {(() => {
                      const d = memory.created_at ? new Date(memory.created_at) : null;
                      if (!d || isNaN(d.getTime())) return (
                        <span className="font-['Outfit'] uppercase" style={{ color: `${theme.accent}80`, fontSize: 9 }}>—</span>
                      );
                      const day   = d.getDate();
                      const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                      const year  = String(d.getFullYear()).slice(-2);
                      return (
                        <>
                          <span className="font-['Cormorant_Garamond'] font-bold leading-none"
                            style={{ color: theme.accent, fontSize: 18 }}>{day}</span>
                          <span className="font-['Outfit'] uppercase tracking-[0.18em] leading-none mt-0.5"
                            style={{ color: `${theme.accent}cc`, fontSize: 8 }}>{month}</span>
                          <span className="font-['Outfit'] tracking-[0.16em] leading-none mt-0.5"
                            style={{ color: `${theme.accent}88`, fontSize: 7.5 }}>’{year}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {showUploadModal && (
        <ImageCropUpload category={category} onClose={() => setShowUploadModal(false)} onSuccess={handleUploadSuccess} />
      )}

      {/* Pillar Path — this category's own themed volume */}
      {showPillarPath && (
        <SovereignsPath
          pillarId={category}
          onClose={() => setShowPillarPath(false)}
        />
      )}
    </div>
  );
};
